import { useEffect, useState } from 'react';
import { ConfirmDialog } from '../ConfirmDialog.js';
import { ErrorBanner } from '../FormBits.js';
import { Modal } from '../ui/modal.js';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select.js';
import { Table, THeadRow, TH, TD, TR } from '../ui/table.js';
import { fmtLimit } from '../PolicyControlTable.js';
import { fmtMonthLong } from '../../utils.js';
import { policy, type RuleInput } from './policyApi.js';
import { PolicyDocumentDialog } from './PolicyDocumentDialog.js';
import {
  POLICY_DENOMINATOR_LABEL_HE, POLICY_MEASURE_BY_ID, SLEEVES, policyComponentsLabelHe,
  type PolicyMeasure, type PolicyReportRow, type PolicyResultStatus, type PolicyRuleRow, type PolicyRuleSaved,
} from '@agio/core/domain';

/**
 * The client's policy, one rule per line. A rule names a measure from the
 * fixed catalogue — or, as a "combined line", the sleeves to add together —
 * what it is measured against, and a floor and/or ceiling.
 * Limits are entered the way the policy statement writes them (25, not 0.25)
 * and stored as fractions.
 */
export interface FundOpt { fund_id: string; fund_name: string; is_visible: boolean }

/** Where each rule stands in the most recent month that was computed. */
interface LatestStatus { period: string; status: PolicyReportRow['status']; byRule: Map<string, PolicyResultStatus> }

/**
 * One line of the policy with every version it has had, oldest first. The
 * version shown on the line is the one in force now (no end month), or the
 * last one when the line has ended.
 */
interface PolicyLine { current: PolicyRuleRow; earlier: PolicyRuleRow[] }

function linesOf(rules: PolicyRuleRow[]): PolicyLine[] {
  const byLine = new Map<string, PolicyRuleRow[]>();
  for (const r of rules) byLine.set(r.line_id, [...(byLine.get(r.line_id) ?? []), r]);
  return [...byLine.values()].map((vs) => {
    const sorted = [...vs].sort((a, b) => a.effective_from.localeCompare(b.effective_from));
    const current = sorted.find((v) => v.effective_to === null) ?? sorted[sorted.length - 1];
    return { current, earlier: sorted.filter((v) => v !== current) };
  });
}

const nextMonthOf = (period: string): string => {
  const [y, m] = period.split('-').map(Number);
  return m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, '0')}`;
};

const span = (r: PolicyRuleRow) =>
  `${fmtMonthLong(r.effective_from)}${r.effective_to ? ` – ${fmtMonthLong(r.effective_to)}` : ' ואילך'}`;

export function PolicyRulesTab({ clientId, funds, rules, onChanged }: {
  clientId: string; funds: FundOpt[]; rules: PolicyRuleRow[]; onChanged: () => void;
}) {
  const [editing, setEditing] = useState<{ rule: PolicyRuleRow; mode: SaveMode } | 'new' | null>(null);
  const [ending, setEnding] = useState<PolicyRuleRow | null>(null);
  const [doomed, setDoomed] = useState<PolicyRuleRow | null>(null);
  const [stale, setStale] = useState<PolicyRuleSaved['stale'] | null>(null);
  const [fromDocument, setFromDocument] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [latest, setLatest] = useState<LatestStatus | null>(null);
  const [reports, setReports] = useState<PolicyReportRow[]>([]);

  // The סיכום line of each rule in the newest computed month, so the list
  // itself says where the portfolio is off policy — not only the month screen.
  useEffect(() => {
    let live = true;
    setLatest(null);
    policy.months(clientId).then(async (m) => {
      if (!live) return;
      setReports(m.reports);
      const newest = m.reports[0];
      if (!newest) return;
      const view = await policy.report(clientId, newest.period);
      if (!view || !live) return;
      const byRule = new Map<string, PolicyResultStatus>();
      for (const r of view.results) if (r.fund_id === null) byRule.set(r.rule_id, r.status);
      setLatest({ period: newest.period, status: newest.status, byRule });
    }).catch((e: Error) => live && setError(e.message));
    return () => { live = false; };
  }, [clientId, rules]);

  const fundName = (id: string) => funds.find((f) => f.fund_id === id)?.fund_name ?? id.split('|').slice(1).join('|');
  const breaches = latest ? [...latest.byRule.values()].filter((s) => s === 'breach').length : 0;
  const lines = linesOf(rules);
  // A policy change lands on the report being prepared: the month after the last one published.
  const lastPublished = reports.find((r) => r.status === 'published');
  const firstOpenMonth = lastPublished ? nextMonthOf(lastPublished.period) : thisMonth();
  const saved = (res: PolicyRuleSaved) => {
    setError(null);
    onChanged();
    if (res.stale.length > 0) setStale(res.stale);
  };

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          כל כלל נבדק מדי חודש לכל תיק ולסיכום התיק. כשהמדיניות משתנה, החודשים שלפני השינוי ממשיכים להיבדק לפי המגבלה הקודמת.
        </p>
        {rules.length > 0 && (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setFromDocument(true)}
              className="rounded-md border border-border px-4 py-1.5 text-sm font-semibold hover:bg-muted"
            >
              קריאה ממסמך
            </button>
            <button
              type="button"
              onClick={() => setEditing('new')}
              className="rounded-md bg-primary px-4 py-1.5 text-sm font-semibold text-primary-foreground"
            >
              + כלל חדש
            </button>
          </div>
        )}
      </div>
      <ErrorBanner error={error} title="שגיאה" />
      {latest && breaches > 0 && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm">
          <span className="font-semibold text-destructive">התיק חורג מהמדיניות</span>{' '}
          ב-{breaches === 1 ? 'כלל אחד' : `${breaches} כללים`} בחודש {fmtMonthLong(latest.period)}
          {latest.status === 'draft' ? ' (טיוטה, טרם פורסם)' : ''}. הפרטים בלשונית "בקרה חודשית".
        </div>
      )}
      {rules.length === 0 ? (
        <div className="grid justify-items-center gap-3 rounded-lg border border-dashed border-border px-6 py-12 text-center">
          <p className="text-base font-semibold">עדיין אין כללי מדיניות ללקוח זה</p>
          <p className="max-w-lg text-sm text-muted-foreground">
            הוסיפו את המגבלות ממסמך מדיניות ההשקעות של הלקוח — למשל "מניות עד 25%", "הצמדה למדד לפחות 50% מסך האג"ח" או "חשיפה למט"ח עד 15%".
            אחרי שיש לפחות כלל אחד אפשר לחשב את הבקרה החודשית ולפרסם אותה ללקוח.
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            <button
              type="button"
              onClick={() => setFromDocument(true)}
              className="rounded-md bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground"
            >
              העלאת מסמך המדיניות
            </button>
            <button
              type="button"
              onClick={() => setEditing('new')}
              className="rounded-md border border-border px-5 py-2 text-sm font-semibold hover:bg-muted"
            >
              + הוספת כלל ידנית
            </button>
          </div>
          <p className="text-xs text-muted-foreground">קובץ PDF, גיליון Excel או מסמך Word — המערכת תציע את הכללים שכתובים במסמך, ואתם מאשרים.</p>
        </div>
      ) : (
        <Table minWidth="60rem">
          <THeadRow>
            <TH>כלל</TH><TH>מדד</TH><TH>ביחס ל</TH><TH>מגבלה</TH><TH>תיקים</TH><TH>בתוקף</TH>
            <TH>{latest ? `מצב ${fmtMonthLong(latest.period)}` : 'מצב'}</TH><TH />
          </THeadRow>
          <tbody>
            {lines.map(({ current: r, earlier }) => {
              const def = POLICY_MEASURE_BY_ID[r.measure];
              // The newest month may have been judged by an earlier version of the line.
              const st = [r, ...earlier].map((v) => latest?.byRule.get(v.rule_id)).find(Boolean);
              return (
                <TR key={r.line_id}>
                  <TD className="font-medium">
                    {r.label_he}
                    {earlier.length > 0 && (
                      <ul className="mt-1 grid gap-0.5 text-xs font-normal text-muted-foreground">
                        {[...earlier].reverse().map((v) => (
                          <li key={v.rule_id}>
                            קודם: {fmtLimit({ unit: POLICY_MEASURE_BY_ID[v.measure]?.unit ?? 'pct', limit_min: v.limit_min, limit_max: v.limit_max })}
                            <span className="mx-1">·</span><span className="tabular-nums">{span(v)}</span>
                            <span className="mx-1">·</span>
                            <button type="button" className="text-primary hover:underline" onClick={() => setEditing({ rule: v, mode: 'fix' })}>תיקון</button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </TD>
                  <TD>
                    {r.components ? policyComponentsLabelHe(r.components) : def?.labelHe ?? r.measure}
                    {def?.source === 'positions' && (
                      <span className="ms-1 text-xs text-warning" title="מחושב מפירוט ניירות הערך בדוח החודשי">*</span>
                    )}
                  </TD>
                  <TD>{def?.unit === 'years' ? 'שנים' : POLICY_DENOMINATOR_LABEL_HE[r.denominator]}</TD>
                  <TD className="tabular-nums">{fmtLimit({ unit: def?.unit ?? 'pct', limit_min: r.limit_min, limit_max: r.limit_max })}</TD>
                  <TD className="text-xs">{r.fund_ids ? r.fund_ids.map(fundName).join(', ') : 'כל התיקים'}</TD>
                  <TD className="text-xs tabular-nums">{span(r)}</TD>
                  <TD className="whitespace-nowrap text-xs">
                    {st === 'breach' && <span className="rounded-full bg-destructive/15 px-2 py-0.5 font-semibold text-destructive">חריגה</span>}
                    {st === 'ok' && <span className="rounded-full bg-success/15 px-2 py-0.5 font-semibold text-success">תקין</span>}
                    {st === 'no_data' && <span className="rounded-full bg-warning/15 px-2 py-0.5 font-semibold text-warning">אין נתון</span>}
                    {!st && <span className="text-muted-foreground">טרם חושב</span>}
                  </TD>
                  <TD className="whitespace-nowrap text-xs">
                    <button type="button" className="text-primary hover:underline" onClick={() => setEditing({ rule: r, mode: r.effective_to === null ? 'change' : 'fix' })}>עריכה</button>
                    <span className="mx-2 text-muted-foreground">·</span>
                    <button type="button" className="text-primary hover:underline" onClick={() => setEnding(r)}>{r.effective_to ? 'הארכה' : 'סיום'}</button>
                    <span className="mx-2 text-muted-foreground">·</span>
                    <button type="button" className="text-destructive hover:underline" onClick={() => setDoomed(r)}>מחיקה</button>
                  </TD>
                </TR>
              );
            })}
          </tbody>
        </Table>
      )}
      <p className="text-xs text-muted-foreground">
        * מדדים המסומנים בכוכבית מחושבים מפירוט ניירות הערך שבדוח הבנק (גידור מט"ח, דירוגים, מח"מ). מח"מ והסכום המנוטרל נלקחים קודם כפי שהם מופיעים בדוח הבנק (שורת הסיכום, קבוצות וניירות מנוטרלים), ואם אין דוח לחודש — מהמאסטר (תיבת "מחמ" ותא "חו"ל מנוטרל מטבע"). כשאין נתון בשום מקום, השורה תסומן "אין נתון" ותדרוש הזנה ידנית לפני פרסום.
      </p>

      {editing && (
        <RuleDialog
          clientId={clientId}
          funds={funds}
          rule={editing === 'new' ? null : editing.rule}
          initialMode={editing === 'new' ? 'fix' : editing.mode}
          firstOpenMonth={firstOpenMonth}
          onClose={() => setEditing(null)}
          onSaved={(res) => { setEditing(null); saved(res); }}
        />
      )}
      {ending && (
        <EndDialog
          clientId={clientId}
          rule={ending}
          firstOpenMonth={firstOpenMonth}
          onClose={() => setEnding(null)}
          onSaved={(res) => { setEnding(null); saved(res); }}
        />
      )}
      {stale && <StaleMonthsDialog clientId={clientId} months={stale} onClose={() => { setStale(null); onChanged(); }} />}
      {fromDocument && (
        <PolicyDocumentDialog
          clientId={clientId}
          rules={rules}
          onClose={() => setFromDocument(false)}
          onSaved={(s) => { setFromDocument(false); onChanged(); if (s.length > 0) setStale(s); }}
        />
      )}
      <ConfirmDialog
        open={!!doomed}
        title="למחוק את הכלל?"
        confirmLabel="מחק"
        tone="danger"
        onCancel={() => setDoomed(null)}
        onConfirm={() => {
          const r = doomed!;
          setDoomed(null);
          policy.deleteRule(clientId, r.rule_id).then(onChanged).catch((e: Error) => setError(e.message));
        }}
      >
        <p>
          <span className="font-semibold">{doomed?.label_he}</span> יימחק, כאילו לא היה. מחיקה מיועדת לכלל שנוסף בטעות —
          כלל שכבר נבדק בבקרה שפורסמה ללקוח אי אפשר למחוק; במקום זה מסיימים אותו מחודש מסוים.
          {lines.some((l) => l.earlier.some((v) => v.line_id === doomed?.line_id)) && ' מחיקת הגרסה האחרונה מבטלת את השינוי: הגרסה הקודמת תמשיך לחול.'}
        </p>
      </ConfirmDialog>
    </div>
  );
}

const MEASURE_GROUPS: { title: string; measures: PolicyMeasure[] }[] = [
  { title: 'מניות', measures: ['stocks', 'stocks_il', 'stocks_foreign', 'emerging_markets'] },
  { title: 'אג"ח', measures: ['bonds_corp', 'bonds_corp_incl_foreign', 'bonds_foreign', 'bonds_foreign_corp', 'bonds_gov', 'bonds_cpi', 'bonds_il', 'bonds_all'] },
  { title: 'דירוג ומח"מ', measures: ['rating_below_a_minus', 'rating_bbb', 'rating_unrated', 'duration'] },
  { title: 'מט"ח ונזילות', measures: ['fx_exposure', 'fx_hedged', 'liquidity', 'commodities'] },
  { title: 'כמה אפיקים יחד', measures: ['combined'] },
];

const thisMonth = () => new Date().toISOString().slice(0, 7);
/** 25 ⇄ 0.25 for percentage measures; years pass through. */
const toInput = (unit: 'pct' | 'years', v: number | null) => (v === null ? '' : unit === 'pct' ? String(+(v * 100).toFixed(4)) : String(v));
const fromInput = (unit: 'pct' | 'years', s: string): number | null => {
  const t = s.trim();
  if (!t) return null;
  const n = Number(t);
  return unit === 'pct' ? n / 100 : n;
};

/**
 * How a save to an existing rule lands. "change": the policy itself changed
 * from a month on — earlier months keep the old values. "fix": the version
 * was entered wrong — every month it covers takes the corrected values.
 */
type SaveMode = 'change' | 'fix';

function RuleDialog({ clientId, funds, rule, initialMode, firstOpenMonth, onClose, onSaved }: {
  clientId: string; funds: FundOpt[]; rule: PolicyRuleRow | null; initialMode: SaveMode; firstOpenMonth: string;
  onClose: () => void; onSaved: (res: PolicyRuleSaved) => void;
}) {
  const [mode, setMode] = useState<SaveMode>(rule ? initialMode : 'fix');
  // A change can only start inside the version, after its first month.
  const startOfChange = rule && firstOpenMonth > rule.effective_from.slice(0, 7) ? firstOpenMonth : thisMonth();
  const [changeFrom, setChangeFrom] = useState(startOfChange);
  const [label, setLabel] = useState(rule?.label_he ?? '');
  const [measure, setMeasure] = useState<PolicyMeasure>(rule?.measure ?? 'stocks');
  const def = POLICY_MEASURE_BY_ID[measure];
  const [components, setComponents] = useState<Set<string>>(new Set(rule?.components ?? []));
  const [denominator, setDenominator] = useState<string>(rule?.denominator ?? 'total');
  const [min, setMin] = useState(toInput(def.unit, rule?.limit_min ?? null));
  const [max, setMax] = useState(toInput(def.unit, rule?.limit_max ?? null));
  const [allFunds, setAllFunds] = useState(rule ? rule.fund_ids === null : true);
  const [picked, setPicked] = useState<Set<string>>(new Set(rule?.fund_ids ?? []));
  const [from, setFrom] = useState(rule?.effective_from.slice(0, 7) ?? thisMonth());
  const [to, setTo] = useState(rule?.effective_to?.slice(0, 7) ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    const body: RuleInput = {
      label_he: label,
      measure,
      // In the order the sleeves are listed everywhere else, not the order they were ticked.
      components: measure === 'combined' ? SLEEVES.map((s) => s.column).filter((c) => components.has(c)) : null,
      denominator: def.unit === 'years' ? 'total' : denominator,
      limit_min: fromInput(def.unit, min),
      limit_max: fromInput(def.unit, max),
      fund_ids: allFunds ? null : [...picked],
      effective_from: from,
      effective_to: to || null,
      sort_order: rule?.sort_order ?? 0,
    };
    try {
      if (!rule) onSaved({ rule: await policy.createRule(clientId, body), stale: [] });
      else if (mode === 'change') onSaved(await policy.changeRule(clientId, rule.rule_id, { ...body, effective_from: changeFrom }));
      else onSaved(await policy.updateRule(clientId, rule.rule_id, body));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const field = 'w-full rounded-md border border-border bg-popover px-3 py-2 text-sm';
  const unitLabel = def.unit === 'years' ? 'שנים' : '%';

  return (
    <Modal
      open
      title={rule ? 'עריכת כלל' : 'כלל מדיניות חדש'}
      description={def.descriptionHe}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <button type="button" onClick={onClose} className="rounded-md px-4 py-2 text-sm text-muted-foreground hover:bg-muted">ביטול</button>
          <button type="button" disabled={busy} onClick={submit} className="rounded-md bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-60">
            {busy ? 'שומר…' : 'שמירה'}
          </button>
        </>
      }
    >
      <div className="grid gap-4">
        {rule && (
          <fieldset className="grid gap-2 rounded-lg border border-border p-3 text-sm">
            <legend className="px-1 text-muted-foreground">מה קרה?</legend>
            <label className="flex items-start gap-2">
              <input type="radio" className="mt-1" checked={mode === 'change'} onChange={() => setMode('change')} />
              <span>
                <span className="font-semibold">המדיניות השתנתה</span>
                <span className="block text-xs text-muted-foreground">
                  הערכים החדשים חלים מהחודש שתבחרו. החודשים שלפניו ממשיכים להיבדק לפי {fmtLimit({ unit: POLICY_MEASURE_BY_ID[rule.measure]?.unit ?? 'pct', limit_min: rule.limit_min, limit_max: rule.limit_max })}.
                </span>
              </span>
            </label>
            {mode === 'change' && (
              <label className="grid gap-1 ps-6 sm:max-w-xs">
                <span className="text-muted-foreground">בתוקף מחודש</span>
                <input type="month" className={field} dir="ltr" value={changeFrom} onChange={(e) => setChangeFrom(e.target.value)} />
              </label>
            )}
            <label className="flex items-start gap-2">
              <input type="radio" className="mt-1" checked={mode === 'fix'} onChange={() => setMode('fix')} />
              <span>
                <span className="font-semibold">תיקון טעות בגרסה הזו</span>
                <span className="block text-xs text-muted-foreground">
                  הכלל הוזן לא נכון. התיקון חל על כל החודשים של הגרסה ({span(rule)}). חודשים שכבר פורסמו לא ישתנו עד שתחשבו אותם מחדש.
                </span>
              </span>
            </label>
          </fieldset>
        )}
        <label className="grid gap-1 text-sm">
          <span className="text-muted-foreground">שם הכלל (כפי שיופיע בדוח)</span>
          <input className={field} value={label} onChange={(e) => setLabel(e.target.value)} placeholder='למשל: מניות עד' />
        </label>
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="grid gap-1 text-sm">
            <span className="text-muted-foreground">מדד</span>
            <Select dir="rtl" value={measure} onValueChange={(v) => { setMeasure(v as PolicyMeasure); setMin(''); setMax(''); }}>
              <SelectTrigger className="bg-popover"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MEASURE_GROUPS.map((g) => (
                  <div key={g.title}>
                    <div className="px-2 pt-2 pb-1 text-xs text-muted-foreground">{g.title}</div>
                    {g.measures.map((m) => (
                      <SelectItem key={m} value={m}>{POLICY_MEASURE_BY_ID[m].labelHe}{POLICY_MEASURE_BY_ID[m].source === 'positions' ? ' *' : ''}</SelectItem>
                    ))}
                  </div>
                ))}
              </SelectContent>
            </Select>
          </label>
          {def.unit === 'pct' && (
            <label className="grid gap-1 text-sm">
              <span className="text-muted-foreground">ביחס ל</span>
              <Select dir="rtl" value={denominator} onValueChange={setDenominator}>
                <SelectTrigger className="bg-popover"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(POLICY_DENOMINATOR_LABEL_HE).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </label>
          )}
        </div>
        {measure === 'combined' && (
          <fieldset className="grid gap-2 text-sm">
            <legend className="mb-2 text-muted-foreground">האפיקים שהשורה מחברת (לפחות שניים)</legend>
            <div className="grid gap-1 sm:grid-cols-2">
              {SLEEVES.map((s) => (
                <label key={s.column} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={components.has(s.column)}
                    onChange={(e) => {
                      const next = new Set(components);
                      if (e.target.checked) next.add(s.column); else next.delete(s.column);
                      setComponents(next);
                    }}
                  />
                  {s.displayNameHe}
                </label>
              ))}
            </div>
          </fieldset>
        )}
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="grid gap-1 text-sm">
            <span className="text-muted-foreground">לפחות ({unitLabel})</span>
            <input className={field} dir="ltr" inputMode="decimal" value={min} onChange={(e) => setMin(e.target.value)} placeholder="ריק = ללא מינימום" />
          </label>
          <label className="grid gap-1 text-sm">
            <span className="text-muted-foreground">עד ({unitLabel})</span>
            <input className={field} dir="ltr" inputMode="decimal" value={max} onChange={(e) => setMax(e.target.value)} placeholder="ריק = ללא מקסימום" />
          </label>
        </div>
        <div className="grid gap-2 text-sm">
          <span className="text-muted-foreground">חל על</span>
          <label className="flex items-center gap-2">
            <input type="radio" checked={allFunds} onChange={() => setAllFunds(true)} /> כל התיקים
          </label>
          <label className="flex items-center gap-2">
            <input type="radio" checked={!allFunds} onChange={() => setAllFunds(false)} /> תיקים נבחרים
          </label>
          {!allFunds && (
            <div className="grid gap-1 ps-6 sm:grid-cols-2">
              {funds.filter((f) => f.is_visible).map((f) => (
                <label key={f.fund_id} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={picked.has(f.fund_id)}
                    onChange={(e) => {
                      const next = new Set(picked);
                      if (e.target.checked) next.add(f.fund_id); else next.delete(f.fund_id);
                      setPicked(next);
                    }}
                  />
                  {f.fund_name}
                </label>
              ))}
            </div>
          )}
        </div>
        {mode === 'fix' && (
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="grid gap-1 text-sm">
              <span className="text-muted-foreground">בתוקף מחודש</span>
              <input type="month" className={field} dir="ltr" value={from} onChange={(e) => setFrom(e.target.value)} />
            </label>
            <label className="grid gap-1 text-sm">
              <span className="text-muted-foreground">עד חודש (ריק = ללא סיום)</span>
              <input type="month" className={field} dir="ltr" value={to} onChange={(e) => setTo(e.target.value)} />
            </label>
          </div>
        )}
        <ErrorBanner error={error} title="לא נשמר" />
      </div>
    </Modal>
  );
}

/** The line stops applying after a month — or, for a line that has ended, applies again with no end. */
function EndDialog({ clientId, rule, firstOpenMonth, onClose, onSaved }: {
  clientId: string; rule: PolicyRuleRow; firstOpenMonth: string; onClose: () => void; onSaved: (res: PolicyRuleSaved) => void;
}) {
  const reopening = rule.effective_to !== null;
  // Ending from the report being prepared means the last month in force is the one before it.
  const [y, m] = firstOpenMonth.split('-').map(Number);
  const before = m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, '0')}`;
  const [last, setLast] = useState(before < rule.effective_from.slice(0, 7) ? rule.effective_from.slice(0, 7) : before);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async () => {
    setBusy(true);
    setError(null);
    try { onSaved(await policy.endRule(clientId, rule.rule_id, reopening ? null : last)); }
    catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };
  return (
    <Modal
      open
      title={reopening ? 'הארכת הכלל' : 'סיום הכלל'}
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose} className="rounded-md px-4 py-2 text-sm text-muted-foreground hover:bg-muted">ביטול</button>
          <button type="button" disabled={busy || (!reopening && !last)} onClick={submit} className="rounded-md bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-60">
            {busy ? 'שומר…' : reopening ? 'הארכה ללא סיום' : 'סיום'}
          </button>
        </>
      }
    >
      <div className="grid gap-3 text-sm">
        {reopening ? (
          <p><span className="font-semibold">{rule.label_he}</span> הסתיים ב-{fmtMonthLong(rule.effective_to!)}. הארכה תחיל אותו שוב, ללא חודש סיום.</p>
        ) : (
          <>
            <p><span className="font-semibold">{rule.label_he}</span> יפסיק לחול אחרי החודש שתבחרו. החודשים עד אליו ממשיכים להיבדק לפיו.</p>
            <label className="grid gap-1 sm:max-w-xs">
              <span className="text-muted-foreground">החודש האחרון שבו הכלל חל</span>
              <input type="month" className="w-full rounded-md border border-border bg-popover px-3 py-2 text-sm" dir="ltr" value={last} onChange={(e) => setLast(e.target.value)} />
            </label>
          </>
        )}
        <ErrorBanner error={error} title="לא נשמר" />
      </div>
    </Modal>
  );
}

/**
 * After a save, the months already computed that were judged by what was
 * there before. Drafts are ticked (nothing was shown to the client yet);
 * published months are not — recomputing one takes it off the client's
 * screen until it is published again, so that is the admin's call.
 */
function StaleMonthsDialog({ clientId, months, onClose }: {
  clientId: string; months: PolicyRuleSaved['stale']; onClose: () => void;
}) {
  const [ticked, setTicked] = useState<Set<string>>(new Set(months.filter((m) => m.status === 'draft').map((m) => m.period)));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const published = months.filter((m) => m.status === 'published').length;
  const recompute = async () => {
    setBusy(true);
    setError(null);
    try {
      for (const p of months.filter((m) => ticked.has(m.period))) {
        try { await policy.compute(clientId, p.period); }
        catch (e) { throw new Error(`${fmtMonthLong(p.period)}: ${(e as Error).message}`, { cause: e }); }
      }
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      open
      title="חודשים שחושבו לפי הכלל הקודם"
      description="הבקרה של החודשים האלה חושבה לפני השינוי. סמנו את החודשים לחישוב מחדש."
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose} className="rounded-md px-4 py-2 text-sm text-muted-foreground hover:bg-muted">לא עכשיו</button>
          <button type="button" disabled={busy || ticked.size === 0} onClick={recompute} className="rounded-md bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-60">
            {busy ? 'מחשב…' : `חישוב מחדש (${ticked.size})`}
          </button>
        </>
      }
    >
      <div className="grid gap-3 text-sm">
        <ul className="grid gap-1">
          {months.map((m) => (
            <li key={m.period}>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={ticked.has(m.period)}
                  onChange={(e) => {
                    const next = new Set(ticked);
                    if (e.target.checked) next.add(m.period); else next.delete(m.period);
                    setTicked(next);
                  }}
                />
                <span className="tabular-nums">{fmtMonthLong(m.period)}</span>
                <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${m.status === 'published' ? 'bg-success/15 text-success' : 'bg-muted text-muted-foreground'}`}>
                  {m.status === 'published' ? 'פורסם' : 'טיוטה'}
                </span>
              </label>
            </li>
          ))}
        </ul>
        {published > 0 && (
          <p className="rounded-md bg-warning/10 p-3 text-xs text-warning">
            חודש שפורסם וחושב מחדש חוזר לטיוטה ויורד ממסך הלקוח עד שיפורסם שוב. בלי חישוב מחדש הלקוח ממשיך לראות את מה שפורסם.
          </p>
        )}
        <ErrorBanner error={error} title="החישוב לא הושלם" />
      </div>
    </Modal>
  );
}
